import Image from 'next/image';
import { useIntl } from 'react-intl';

import { Flex } from '@/components/Flex';
import { LegacyButton } from '@/components/LegacyButton';
import { Modal } from '@/components/Modal';
import { Typography } from '@/components/Typography';
import type { Money } from '@/graphql/saleor/api';
import type { Maybe } from '@/lib/types';
import {
  FixedAprPercent,
  OneyDownPaymentMessage,
  PaymentCost,
} from '@/messages/common';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import OneyIcon from '@/assets/icons/oney.svg';

import { messages, OneyDescription } from './messages';
import * as S from './OneyModal.styles';
export interface OneyModalProps {
  isOpen: boolean;
  onClose: () => void;
  price: Maybe<Money>;
}
export const OneyModal = ({ isOpen, onClose, price }: OneyModalProps) => {
  const { formatPrice } = useRegionProvider();
  const { formatMessage } = useIntl();
  const { oney3xPaymentMax, oney3xPaymentMin } = useConfigProvider();

  const times = [3, 4];

  return (
    <Modal
      data-test-id='OneyModal-Modal'
      isOpen={isOpen}
      maxWidth='sm'
      onClose={onClose}
    >
      <Modal.Header data-test-id='OneyModal-ModalHeader' onClose={onClose}>
        <S.Header>
          <Image alt='oney' height={40} src={OneyIcon} width={100} />
          <S.HeaderText>{formatMessage(messages.payment)}</S.HeaderText>
          <S.HeaderText>{formatMessage(messages.byCreditCard)}</S.HeaderText>
        </S.Header>
      </Modal.Header>
      <Modal.Body data-test-id='OneyModal-ModalBody'>
        <S.Body data-test-id='OneyModal-Body'>
          {!!price &&
            times.map((el) => (
              <Flex key={el} alignItems='center' gridGap='s200' padding='s100'>
                <S.Oney3xBadge>
                  <Typography>{el}x</Typography>
                </S.Oney3xBadge>
                <Flex flexDirection='column'>
                  <Typography>
                    {OneyDownPaymentMessage(
                      formatPrice(
                        {
                          amount: price.amount / el,
                          currency: price.currency,
                        },
                        {
                          currencyDisplay: 'symbol',
                        },
                      ),
                      el - 1,
                    )}
                  </Typography>
                  <Typography>{FixedAprPercent(0)}</Typography>
                  <Typography>
                    {PaymentCost(
                      formatPrice(
                        {
                          amount: 0,
                          currency: price.currency,
                        },
                        { currencyDisplay: 'symbol' },
                      ),
                    )}
                  </Typography>
                </Flex>
              </Flex>
            ))}
          {!!price && (
            <Typography marginTop='s100'>
              {OneyDescription(
                formatPrice(
                  {
                    amount: oney3xPaymentMin,
                    currency: price.currency,
                  },
                  { currencyDisplay: 'symbol' },
                ),
                formatPrice(
                  {
                    amount: oney3xPaymentMax,
                    currency: price.currency,
                  },
                  { currencyDisplay: 'symbol' },
                ),
              )}
            </Typography>
          )}
          <Typography marginTop='s100'>
            {formatMessage(messages.oneyBank)}
          </Typography>
          <Typography>{formatMessage(messages.oneyHeadOffice)}</Typography>
          <Typography>{formatMessage(messages.oneyCorrespondence)}</Typography>
          <LegacyButton
            data-testid='oneyModalOk'
            label='OK'
            marginTop='s100'
            onClick={onClose}
          />
        </S.Body>
      </Modal.Body>
    </Modal>
  );
};
